import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { callAPI } from '../helper/apiUtils';
import './TranslationProgressModal.css';

const LANGUAGE_LABELS = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  it: 'Italiano',
  es: 'Español',
  pt: 'Português',
  ru: 'Русский',
  pl: 'Polski',
  nl: 'Nederlands',
  tr: 'Türkçe',
  el: 'Ελληνικά',
  cs: 'Čeština',
  hu: 'Magyar',
  ro: 'Română',
  bg: 'Български',
  hr: 'Hrvatski',
  sl: 'Slovenščina (Slovenian)',
  sk: 'Slovenčina (Slovak)',
  sv: 'Svenska',
  no: 'Norsk',
  da: 'Dansk',
  fi: 'Suomi',
  zh: '中文',
  hi: 'हिन्दी',
  ar: 'العربية',
  ja: '日本語',
  ko: '한국어',
  vi: 'Tiếng Việt',
  id: 'Bahasa Indonesia',
  ms: 'Bahasa Melayu',
  bn: 'বাংলা',
  ta: 'தமிழ்',
  te: 'తెలుగు',
  mr: 'मराठी',
};

export default function TranslationProgressModal({ show, articleId, onComplete, strings = {} }) {
  const [targetLanguages, setTargetLanguages] = useState([]);
  const [completedLanguages, setCompletedLanguages] = useState([]);
  const [done, setDone] = useState(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!show || !articleId) return;

    setDone(false);
    setCompletedLanguages([]);
    setTargetLanguages([]);

    const poll = async () => {
      try {
        const res = await callAPI(`/article/${articleId}/translationStatus`, {}, 'GET');
        if (!mountedRef.current) return;
        const data = res?.data;
        if (data?.status) {
          setTargetLanguages(data.targetLanguages || []);
          setCompletedLanguages(data.completedLanguages || []);
          if (data.done) {
            setDone(true);
            setTimeout(() => {
              if (mountedRef.current && onComplete) onComplete();
            }, 1500);
            return;
          }
        }
      } catch (_) { /* ignore */ }

      if (mountedRef.current) {
        timerRef.current = setTimeout(poll, 2000);
      }
    };

    timerRef.current = setTimeout(poll, 1500);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [show, articleId]);

  const completedSet = new Set(completedLanguages);
  const currentLang = targetLanguages.find(l => !completedSet.has(l));
  const hasLanguages = targetLanguages.length > 0;
  const stepItems = [
    {
      key: 'prepare',
      label: strings.transition_step_prepare_article || strings.article || 'Article',
      done: hasLanguages || done,
      active: !hasLanguages && !done,
    },
    {
      key: 'translate',
      label: strings.transition_step_translate_content || strings.auto_chat_translation || 'Translating',
      done: done,
      active: hasLanguages && !done,
    },
    {
      key: 'post',
      label: strings.transition_step_post_article || strings.post || 'Post',
      done,
      active: false,
    },
  ];

  return (
    <Modal show={show} centered backdrop="static" keyboard={false}>
      <Modal.Body className="translation-progress-modal__body">
        <p className="translation-progress-modal__title">
          {strings.transition_title_publish || `${strings.post || 'Post'} ${strings.article || 'article'}`}
        </p>
        <p className="translation-progress-modal__subtitle">
          {strings.transition_subtitle_wait || strings.Loading || 'Loading...'}
        </p>

        <div className="translation-progress-modal__steps">
          {stepItems.map((step) => (
            <div
              key={step.key}
              className={`translation-progress-modal__step${step.active ? ' translation-progress-modal__step--active' : ''}${step.done ? ' translation-progress-modal__step--done' : ''}`}
            >
              <span className="material-symbols-outlined translation-progress-modal__step-icon">
                {step.done ? 'check_circle' : step.active ? 'hourglass_top' : 'radio_button_unchecked'}
              </span>
              <span>{step.label}</span>
            </div>
          ))}
        </div>

        {!done && hasLanguages && (
          <p className="translation-progress-modal__current">
            {(strings.transition_active_language || strings.language || 'Language')}: {currentLang ? `${currentLang.toUpperCase()} — ${LANGUAGE_LABELS[currentLang] || currentLang}` : '...'}
          </p>
        )}

        {!done && hasLanguages && (
          <div className="translation-progress-modal__langs">
            {targetLanguages.map(lang => {
              const isCompleted = completedSet.has(lang);
              const isCurrent = lang === currentLang && !done;
              return (
                <div
                  key={lang}
                  className={`translation-progress-modal__lang${isCompleted ? ' translation-progress-modal__lang--completed' : isCurrent ? ' translation-progress-modal__lang--current' : ''}`}
                >
                  <span className="material-symbols-outlined translation-progress-modal__lang-icon">
                    {isCompleted ? 'check' : isCurrent ? 'more_horiz' : 'radio_button_unchecked'}
                  </span>
                  <span>{lang.toUpperCase()} — {LANGUAGE_LABELS[lang] || lang}</span>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
